import { Component, Input, Output, EventEmitter } from '@angular/core';
import { Validators, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { LocalStorageService } from 'ngx-webstorage';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { FieldConfigurationService } from 'src/app/core/service/field-configuration.service';
import { NGXLogger } from 'ngx-logger';
import { cloneDeep } from 'lodash';
import { RuleService } from 'src/app/core/service/rule.service';
import { Base64 } from 'js-base64';

@Component({
  standalone: false,
  selector: 'app-channel-rule-tester',
  templateUrl: './channel-rule-tester.component.html',
  styleUrls: ['./channel-rule-tester.component.scss']
})
export class ChannelRuleTesterComponent
{
  @Input() public queries: any[] = [];
  @Input() public configs: any[] = [];
  @Input() public ruleName = '';

  @Output() public closing = new EventEmitter();

  public ruleTesterOpened = false;
  public ruleTesterForm: any;
  public loading = false;
  public ruleMatched: boolean = undefined;

  private keyXML = 'XML-Rule-Tester';
  private keyXMLSave = 'XML-Rule-Tester-Save';

  constructor(
    private formBuilder: UntypedFormBuilder,
    private storageService: LocalStorageService,
    private messageHandlerService: MessageHandlerService,
    private ruleService: RuleService,
    private fieldConfigurationService: FieldConfigurationService,
    private logger: NGXLogger)
  {
    this.buildForm(false);
  }

  /**
   * Getter for form control field xml.
   */
  public get xml(): UntypedFormControl
  {
    return this.ruleTesterForm.get('xml');
  }

  /**
   * Getter for form control field xml.
   */
  public get xmlSave(): UntypedFormControl
  {
    return this.ruleTesterForm.get('xmlSave');
  }


  @Input() public set dialogOpened(value: boolean)
  {
    this.ruleMatched = undefined;
    this.ruleTesterOpened = value;
    if (value)
    {
      this.loading = this.xmlSaved();   // Only show loading if xml is saved to storage
      document.body.style.overflow = 'hidden';
      setTimeout(() =>
      {
        this.buildForm(true);
      });
    }
  }

  /**
   * Close modal.
   */
  public closeModal(): void
  {
    // Close dialog
    document.body.style.overflow = '';
    this.saveLocalStorage();
    this.ruleTesterOpened = false;
    this.closing.emit();
  }

  /**
   * Run the rule tests
   */
  public testRules(): void
  {
    this.ruleMatched = undefined;
    this.saveLocalStorage();

    // Check if valid xml at minimum
    if (!this.xml.value.includes('<Container>'))
    {
      this.messageHandlerService.displayError('RULE-TESTER-INVALID-XML');
      return;
    }

    // Perform rules check
    let queryRules;
    const queries = cloneDeep(this.queries);

    for (let i = 0; i < queries.length; i++)
    {
      const query = queries[i];
      if (query.rules && query.rules.length > 0)
      {
        // Convert Rules array values to string
        this.ruleService.convertRulesValueArrayToString(query);

        // Remove unused properties
        this.cleanupQueryProperties(query);

        // Set query rules object
        if (queryRules === undefined)
        {
          queryRules = query;
        }
        else
        {
          // Add other rules for other sub-sections
          query.rules.forEach(r =>
          {
            queryRules.rules.push(r);
          });
        }
      }
    }

    // Encode xml to based64
    const based64XML = Base64.encode(this.xml.value);

    // Check rules on the server
    this.fieldConfigurationService.checkField({ xml: based64XML, query: queryRules }).subscribe({
      next: (data) =>
      {
        this.logger.info(data);
        if (data && !data.errorMessage)
        {
          this.ruleMatched = data.matched;
        }
        else
        {
          this.messageHandlerService.displayError('RULE-TESTER-ERROR');
        }
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
      }
    });
  }

  /**
   * Convert recursively Query Rules to remove unused properties. This is required prior call .check method.
   *
   * @param rule Query Rules object
   */
  private cleanupQueryProperties(rule: any)
  {
    delete rule.version;
    delete rule.id;
    delete rule.createdBy;
    delete rule.createdOn;
    delete rule.lastModifiedBy;
    delete rule.lastModifiedOn;
    delete rule.section;
    delete rule.subSection;

    rule.rules.forEach(r =>
    {
      delete r.version;
      delete r.id;
      delete r.createdBy;
      delete r.createdOn;
      delete r.lastModifiedBy;
      delete r.lastModifiedOn;
      delete r.queryRuleItem;
      delete r.queryRule;
      if (!r.condition)
      {
        delete r.condition;
      }
      if (r.rules)
      {
        this.cleanupQueryProperties(r);
      }
    });
  }

  /**
   * Save storage properties. XML content and save switch value.
   */
  private saveLocalStorage(): void
  {
    // Handle storage
    this.storageService.store(this.keyXMLSave, this.xmlSave.value);
    if (this.xmlSave.value)
    {
      this.storageService.store(this.keyXML, this.xml.value);
    }
    else
    {
      this.storageService.clear(this.keyXML);
    }
  }

  private buildForm(loadFromStorage: boolean): void
  {
    let xml = '';
    let xmlSave = false;

    if (loadFromStorage)
    {
      xmlSave = this.xmlSaved();
      xml = this.storageService.retrieve(this.keyXML) || '';
    }

    this.ruleTesterForm = this.formBuilder.group({
      xml: [xml, Validators.required],
      xmlSave: [xmlSave]
    });
    this.loading = false;
  }

  private xmlSaved(): boolean
  {
    return this.storageService.retrieve(this.keyXMLSave) || false;
  }
}
